import { useQueryClient } from "@tanstack/react-query";
import { Search, X } from "lucide-react";
import { useMessageSearch } from "../api/chatApi";
import type { ChatMessage } from "../types";
import { formatDateTime } from "@/utils/formatDate";

interface SearchResultsPanelProps {
  roomId: string;
  query: string;
  onQueryChange: (q: string) => void;
  onClose: () => void;
  onJump: (hit: ChatMessage) => void;
}

function snippet(body: string, term: string): string {
  if (!body) return "";
  const t = term.toLowerCase();
  const lower = body.toLowerCase();
  const idx = lower.indexOf(t);
  if (idx === -1) return body.slice(0, 120);
  const start = Math.max(0, idx - 40);
  const end = Math.min(body.length, idx + term.length + 80);
  return (start > 0 ? "…" : "") + body.slice(start, end) + (end < body.length ? "…" : "");
}

export function SearchResultsPanel({
  roomId,
  query,
  onQueryChange,
  onClose,
  onJump,
}: SearchResultsPanelProps) {
  const qc = useQueryClient();
  const search = useMessageSearch(roomId, query);
  const hits = search.data ?? [];

  return (
    <div className="border-b border-gray-200 bg-white flex-shrink-0">
      <div className="px-3 py-2 flex items-center gap-2 bg-red-50">
        <Search size={14} className="text-gray-500" />
        <input
          autoFocus
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Szukaj w rozmowie (min. 2 znaki)…"
          className="flex-1 bg-transparent text-sm focus:outline-none text-gray-900 placeholder:text-gray-400"
        />
        <span className="text-[11px] text-gray-500 font-mono">
          {query.trim().length < 2 ? "—" : `${hits.length}`}
        </span>
        <button
          type="button"
          onClick={() => {
            onQueryChange("");
            qc.removeQueries({ queryKey: ["chat", "rooms", roomId, "search"] });
            onClose();
          }}
          className="text-gray-500 hover:text-gray-900"
          title="Zamknij"
        >
          <X size={14} />
        </button>
      </div>
      {query.trim().length >= 2 && (
        <div className="max-h-60 overflow-y-auto">
          {search.isFetching && (
            <div className="px-3 py-2 text-[11px] text-gray-500 italic">
              Szukam…
            </div>
          )}
          {!search.isFetching && hits.length === 0 && (
            <div className="px-3 py-2 text-[11px] text-gray-500">
              Brak wyników.
            </div>
          )}
          {hits.map((m) => {
            const senderName =
              m.sender?.full_name || m.sender?.username || "—";
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => onJump(m)}
                className="w-full text-left px-3 py-1.5 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
              >
                <div className="flex items-center justify-between gap-2 text-[11px] text-gray-500">
                  <span className="font-semibold text-gray-700">{senderName}</span>
                  <span className="font-mono">{formatDateTime(m.created_at, "")}</span>
                </div>
                <div className="text-[12px] text-gray-800 truncate">
                  {snippet(m.body, query)}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
